import { useEffect, useRef } from 'react';

interface YouTubePlayerProps {
  videoId: string;
  isPlaying: boolean;
  onEnded?: () => void;
  maxDuration?: number; // Max duration in seconds (default 30)
  onProgress?: (currentTime: number, duration: number) => void;
}

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

export const YouTubePlayer = ({
  videoId,
  isPlaying,
  onEnded,
  maxDuration = 30,
  onProgress,
}: YouTubePlayerProps) => {
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressIntervalRef = useRef<number | null>(null);
  
  // Use refs to store callbacks and avoid re-running effects when they change
  const onProgressRef = useRef(onProgress);
  const onEndedRef = useRef(onEnded);
  
  // Update refs when callbacks change
  useEffect(() => {
    onProgressRef.current = onProgress;
    onEndedRef.current = onEnded;
  }, [onProgress, onEnded]);

  // Load / init player ONCE on mount
  useEffect(() => {
    const loadYouTubeApi = () => {
      if (window.YT && window.YT.Player) return;

      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag?.parentNode?.insertBefore(tag, firstScriptTag);
    };

    const initPlayer = () => {
      if (!containerRef.current) return;
      if (playerRef.current) return; // Don't recreate if already exists

      playerRef.current = new window.YT.Player(containerRef.current, {
        height: '200',
        width: '360',
        videoId,
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
          modestbranding: 1,
          playsinline: 1,
        },
        events: {
          onReady: () => {
            if (onProgressRef.current) {
              try {
                const duration = Math.min(
                  typeof playerRef.current?.getDuration === 'function'
                    ? playerRef.current.getDuration()
                    : maxDuration,
                  maxDuration
                );
                onProgressRef.current(0, duration || maxDuration);
              } catch {
                onProgressRef.current(0, maxDuration);
              }
            }

            if (isPlaying && playerRef.current && typeof playerRef.current.playVideo === 'function') {
              try {
                if (typeof playerRef.current.unMute === 'function') {
                  playerRef.current.unMute();
                }
                if (typeof playerRef.current.setVolume === 'function') {
                  playerRef.current.setVolume(100);
                }
                playerRef.current.playVideo();
              } catch (e) {
                console.warn('YouTube onReady playback error:', e);
              }

              if (onProgressRef.current && !progressIntervalRef.current) {
                progressIntervalRef.current = window.setInterval(() => {
                  try {
                    const currentTime =
                      typeof playerRef.current?.getCurrentTime === 'function'
                        ? playerRef.current.getCurrentTime()
                        : 0;
                    const duration = Math.min(
                      typeof playerRef.current?.getDuration === 'function'
                        ? playerRef.current.getDuration()
                        : maxDuration,
                      maxDuration
                    );

                    if (onProgressRef.current) {
                      onProgressRef.current(currentTime, duration || maxDuration);
                    }

                    if (currentTime >= maxDuration) {
                      try {
                        playerRef.current.pauseVideo();
                      } catch {
                        /* ignore */
                      }
                      if (onEndedRef.current) onEndedRef.current();
                      if (progressIntervalRef.current) {
                        window.clearInterval(progressIntervalRef.current);
                        progressIntervalRef.current = null;
                      }
                    }
                  } catch (e) {
                    console.warn('YouTube onReady progress error:', e);
                  }
                }, 200);
              }
            }
          },
          onStateChange: (event: any) => {
            if (event.data === 0) {
              if (onEndedRef.current) onEndedRef.current();
            }
          },
        },
      });
    };

    if (!window.YT || !window.YT.Player) {
      window.onYouTubeIframeAPIReady = () => {
        initPlayer();
      };
      loadYouTubeApi();
    } else {
      initPlayer();
    }

    // Cleanup ONLY on unmount
    return () => {
      if (progressIntervalRef.current) {
        window.clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }

      if (playerRef.current) {
        try {
          // Check if the iframe still exists before trying to destroy
          const iframe = playerRef.current.getIframe?.();
          if (iframe && iframe.parentNode) {
            if (typeof playerRef.current.destroy === 'function') {
              playerRef.current.destroy();
            }
          }
        } catch (e) {
          // Silently ignore cleanup errors - the iframe may already be gone
        } finally {
          playerRef.current = null;
        }
      }
    };
  }, []); // Only run on mount/unmount

  // Handle videoId changes by cueing new video
  useEffect(() => {
    if (!playerRef.current || !videoId) return;
    
    // Wait for player to be ready before cueing
    const cueNewVideo = () => {
      if (playerRef.current && typeof playerRef.current.cueVideoById === 'function') {
        try {
          playerRef.current.cueVideoById(videoId);
          // Small delay to let the cue complete before playing
          setTimeout(() => {
            if (isPlaying && playerRef.current && typeof playerRef.current.playVideo === 'function') {
              playerRef.current.playVideo();
            }
          }, 100);
        } catch (e) {
          console.warn('YouTube cueVideoById error:', e);
        }
      }
    };

    cueNewVideo();
  }, [videoId, isPlaying]);

  // Control playback & progress based on isPlaying
  useEffect(() => {
    if (!playerRef.current || typeof playerRef.current.playVideo !== 'function') {
      return;
    }

    // Clear any existing interval first
    if (progressIntervalRef.current) {
      window.clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    if (isPlaying) {
      try {
        // Ensure audio is not muted and volume is up when starting
        if (typeof playerRef.current.unMute === 'function') {
          playerRef.current.unMute();
        }
        if (typeof playerRef.current.setVolume === 'function') {
          playerRef.current.setVolume(100);
        }
        playerRef.current.playVideo();
      } catch (e) {
        console.warn('YouTube playback error:', e);
      }

      if (onProgressRef.current) {
        progressIntervalRef.current = window.setInterval(() => {
          try {
            const currentTime =
              typeof playerRef.current?.getCurrentTime === 'function'
                ? playerRef.current.getCurrentTime()
                : 0;
            const duration = Math.min(
              typeof playerRef.current?.getDuration === 'function'
                ? playerRef.current.getDuration()
                : maxDuration,
              maxDuration
            );

            if (onProgressRef.current) {
              onProgressRef.current(currentTime, duration || maxDuration);
            }

            // Hard stop at maxDuration
            if (currentTime >= maxDuration) {
              try {
                playerRef.current.pauseVideo();
              } catch {
                /* ignore */
              }
              if (onEndedRef.current) onEndedRef.current();
              if (progressIntervalRef.current) {
                window.clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
              }
            }
          } catch (e) {
            console.warn('YouTube progress error:', e);
          }
        }, 200);
      }
    } else {
      try {
        playerRef.current.pauseVideo();
      } catch (e) {
        console.warn('YouTube pause error:', e);
      }
    }

    return () => {
      if (progressIntervalRef.current) {
        window.clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [isPlaying, maxDuration, videoId]);

  // Keep the player hidden but functional for audio playback
  // Use a stable container that won't be removed by React
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        right: 0,
        width: 1,
        height: 1,
        overflow: 'hidden',
        opacity: 0,
        pointerEvents: 'none',
        zIndex: -1,
      }}
    >
      <div ref={containerRef} />
    </div>
  );
};
